import React, { useState, useRef } from 'react';
import { AppBarComponent } from '@syncfusion/ej2-react-navigations';
import { ButtonComponent } from '@syncfusion/ej2-react-buttons';
import { SpreadsheetComponent } from '@syncfusion/ej2-react-spreadsheet';
import { IDocumentLoader, IDocumentWoutBody, IDocumentRevisions, saveDocument2Contentum, IResult, IErrorObject, saveDocument2Service } from './types/document';
import { ToastComponent , ToastUtility, Toast } from '@syncfusion/ej2-react-notifications';
import { DialogUtility } from '@syncfusion/ej2-popups';
import AboutDocumentDialog from './about';
import moment from 'moment';
import { L10n } from '@syncfusion/ej2-base';
import excelLocale from './locale/excel.locale.json';
import EditDocumentAbortDialog from './editAbort';
import config from './../config.json';

interface IExcelAppProps {
    enableRTL: boolean;
    documentLoader: IDocumentLoader;
}

function ExcelApp(props: IExcelAppProps) {
    const enableRTL : boolean = props.enableRTL;
    const documentLoader : IDocumentLoader = props.documentLoader;

    let saveType : string = '';

    const spreadsheetRef = useRef<SpreadsheetComponent>(null);

    //https://services.syncfusion.com/react/production/api/spreadsheet
    //const serviceUrl : string = 'https://services.syncfusion.com/react/production/api/spreadsheet/';
    //const serviceUrl : string = 'http://10.0.130.8:6002/api/spreadsheet/';
    const serviceUrl : string = 'https://document.e-contentum.com/api/spreadsheet/';

    const [ allowEdit, setAllowEdit ] = useState <boolean>(documentLoader.document.metadata.isNowCollaborated ? false : documentLoader.document.access.allowEdit);

    const [ documentName, setDocumentName ] = useState <string>('');
    const [ contentChange, setContentChange ] = useState <boolean>(false);
    
    /* Check document edit access periodically */

    const abortController = new AbortController();

    React.useEffect(() => {

        if (!allowEdit) return ;
        if (documentLoader.isExternal) return ;

        let loadFormData : any = new FormData();
        loadFormData.append('sid', documentLoader.requestParams.systemId);
        loadFormData.append('id', documentLoader.requestParams.documentId);
        loadFormData.append('vuid', documentLoader.requestParams.versionUid);
        loadFormData.append('uid', documentLoader.requestParams.userId);

        const id = setInterval(() => {
            fetch(config.settings.baseServerUrl + '/client/api/document/edit/hasaccess', {
                method:"POST",
                signal: abortController.signal,
                body: loadFormData
            })
            .then((response: any) => { 
                if (response.status === 200) {
                    return response.json(); 
                } else {
                    console.log('Error get data, status: ' + response.status + ', text: ' + response.statustext);
                    return null;
                }
            })
            .then((result: any) => { 
                if (!allowEdit) return ;
                if (result) {
                    if (result.success) {
                        if (!result.data) {
                            console.log('Abort document editing');
                            setAllowEdit(false);
                            abortController.abort(); 
                            EditDocumentAbortDialog('УВАГА!','На жаль ви не можете далі редагувати цей документ оскільки були вилучені права на внесення змін до цього документу.')
                        }
                    }
                }
            })
            .catch((error: any) => { 
                console.log(`Error get data: ${error}`); 
                abortController.abort(); 
            });

        }, 1000 * 60 * 3)
        return () => {
            clearInterval(id)
        }
        }, [])
                

    /* Check document edit access */

    let toastInstance: ToastComponent;
    function toastCreated(): void {
        if (documentLoader.document.metadata.isBlocked || !allowEdit)
            toastInstance.show();
    }
    
    function infoToastMessage(message: string) : Toast {
        const toastObj : Toast = ToastUtility.show({
            title: message,
            //content: 'Toast shown using utility function with ToastModel',
            timeOut: 3000,
            position: { X: 'Center', Y: 'Top' },
            showCloseButton: false
        },'Information');
        return toastObj;
    }
    
    function errorToastMessage(message: string, content: string, error: IErrorObject) : Toast {

        function detailsClick(msg: string, error: IErrorObject) : void {
            DialogUtility.alert({
                animationSettings: { effect: 'Zoom' },
                closeOnEscape: true,
                content: '<p>' + msg + '</p><ul><li>' + error.message + '</li>' + '<li>' + error.details as string + '</li></ul>',
                showCloseIcon: true,
                title: 'Інформація щодо помилки'
            });
        }

        const toastObj : Toast = ToastUtility.show({
            title: message,
            content: content,
            timeOut: 5000,
            position: { X: 'Center', Y: 'Top' },
            buttons:  [{
                model: { content: 'Деталі помилки' }, click: () => detailsClick(content,error)
            }]
        });

        return toastObj;
    }

    const beforeSave = (args: any) => {
        args.needBlobData = true; // To trigger the saveComplete event.
        args.isFullPost = false; // Get the spreadsheet data as blob data in the saveComplete event.
    }

    const saveComplete = async (args: any) => {
        if (saveType === 'export') {
            var url = window.URL.createObjectURL(args.blobData);
            const tempLink = document.createElement('a');
            tempLink.href = url;
            tempLink.setAttribute('download', props.documentLoader.document.name + ' (v' + props.documentLoader.document.metadata.versionNumber.toString() + ')' + 
                '.pdf');
            tempLink.click();
            window.URL.revokeObjectURL(url);
        }
        if (saveType === 'download') {
            var url = window.URL.createObjectURL(args.blobData);
            const tempLink = document.createElement('a');
            tempLink.href = url;
            tempLink.setAttribute('download', props.documentLoader.document.name + ' (v' + props.documentLoader.document.metadata.versionNumber.toString() + ')' + 
                '.xlsx');
            tempLink.click();
            window.URL.revokeObjectURL(url);
        }
        if (saveType === 'save') {

            if (!documentLoader.isExternal) {
                const result : IResult = await saveDocument2Contentum({
                        name: documentLoader.document.name,
                        metadata: documentLoader.document.metadata,
                        access: documentLoader.document.access,
                        requestParams: documentLoader.requestParams,
                        user: documentLoader.user
                    } as IDocumentWoutBody,
                    {
                        enabled: false,
                        comments: null!,
                        revisions: null!
                    } as IDocumentRevisions,
                    args.blobData
                );
                setContentChange(false);
                if (result.success)
                    infoToastMessage('Документ збережено');
                else
                    errorToastMessage('На жаль, сталася помилка','При збереженні документа сталася помилка виконання операції на сервері',result.error);
            }

            if (documentLoader.isExternal) {
                const result : IResult = await saveDocument2Service({
                        name: documentLoader.document.name,
                        metadata: documentLoader.document.metadata,
                        access: documentLoader.document.access,
                        requestParams: documentLoader.requestParams,
                        user: documentLoader.user
                    } as IDocumentWoutBody,
                    args.blobData
                );
                setContentChange(false);
                if (result.success)
                    infoToastMessage('Документ збережено');
                else
                    errorToastMessage('На жаль, сталася помилка','При збереженні документа сталася помилка виконання операції на сервері',result.error);
            }

        }
        spreadsheetRef.current!.allowSave = false;
    }

    function saveDocument() : void {
        if (!spreadsheetRef) return ;

        spreadsheetRef.current!.allowSave = true;
        saveType = 'save';

        setTimeout(() => {
            spreadsheetRef.current!.save({
                url: 'https://document.e-contentum.com/api/spreadsheet/save',
                //url: 'https://services.syncfusion.com/react/production/api/spreadsheet/save',
                fileName: props.documentLoader.document.name + ' (v' + props.documentLoader.document.metadata.versionNumber.toString() + ')',
                saveType: 'Xlsx',
            });
        }, 500);
    }    

    function exportDocument2PDF() : void {
        if (!spreadsheetRef) return ;

        spreadsheetRef.current!.allowSave = true;
        saveType = 'export';
        
        setTimeout(() => {
            spreadsheetRef.current!.save({ url: 'https://document.e-contentum.com/api/spreadsheet/save', 
            //spreadsheetRef.current!.save({ url: 'https://services.syncfusion.com/react/production/api/spreadsheet/save', 
                fileName: props.documentLoader.document.name + ' (v' + props.documentLoader.document.metadata.versionNumber.toString() + ')', 
                saveType: "Pdf" }
            );
        }, 500);
    }

    function downloadDocument() : void {
        if (!spreadsheetRef) return ;

        spreadsheetRef.current!.allowSave = true;
        saveType = 'download';
        
        setTimeout(() => {
            spreadsheetRef.current!.save({ url: 'https://services.syncfusion.com/react/production/api/spreadsheet/save', 
                fileName: props.documentLoader.document.name + ' (v' + props.documentLoader.document.metadata.versionNumber.toString() + ')', 
                saveType: "Xlsx" }
            );
        }, 500);
    }
    
    function onCreated() : void {
        if (!spreadsheetRef) return ;

        setDocumentName(documentLoader.document.name);
        spreadsheetRef.current!.allowEditing = allowEdit;

        spreadsheetRef.current!.allowSave = false;//!documentLoader.document.access.readOnly;
        
        spreadsheetRef.current!.hideFileMenuItems(["File"], true); 
        //console.log(props.documentLoader.document.body);
        var data = new Blob([new Uint8Array(props.documentLoader.document.body.data)]);
        const file = new File([data], props.documentLoader.document.metadata.file.name);
        spreadsheetRef.current!.open({ file });
        
    }

    function onCellEdited(args: any) : void {
        setContentChange(true);
    }
    
    L10n.load(excelLocale);

    return (
        <React.Fragment>
            <div className="row" style={{ maxHeight: '40px' }}>
                <div className="col-md-12">
                <AppBarComponent mode="Dense" colorMode="Primary">
                    <AboutDocumentDialog documentLoader={documentLoader}/>
                    <span className="dense">Contentum: {documentName}</span>
                    <div className="e-appbar-spacer"></div>
                    { allowEdit && 
                        <ButtonComponent 
                            cssClass='e-inherit login' 
                            iconCss="e-icons e-save" 
                            onClick={() => { saveDocument() }}
                        >
                            <span style={{ color: contentChange ? '#e3165b' : '' }}>Зберегти</span>
                        </ButtonComponent>
                    }
                    { (documentLoader.document.access.allowExport || documentLoader.document.access.allowDownload) &&
                        <ButtonComponent cssClass='e-inherit login' iconCss="e-icons e-print" onClick={() => { spreadsheetRef.current?.print() }}>Друкувати</ButtonComponent>
                    }
                    { documentLoader.document.access.allowExport &&
                        <ButtonComponent cssClass='e-inherit login' iconCss="e-icons e-export-pdf" onClick={() => { exportDocument2PDF() }}>PDF</ButtonComponent>
                    }
                    { documentLoader.document.access.allowDownload && 
                        <ButtonComponent cssClass='e-inherit login' iconCss="e-icons e-download" onClick={() => { downloadDocument() }}>Завантажити</ButtonComponent>
                    }
                </AppBarComponent>
                </div>
            </div>
            <div id="editorContainerElement">
                <SpreadsheetComponent 
                    ref={spreadsheetRef}
                    cellEdited={onCellEdited}
                    openUrl={serviceUrl + "open"}
                    saveUrl={serviceUrl + "save"}
                    style={{ display: "block" }}
                    //width={window.innerWidth + 'px'}
                    height={window.innerHeight - 73 + 'px'}
                    created={onCreated}
                    locale={'uk-UA'}
                    beforeSave={beforeSave}
                    saveComplete={saveComplete}
                    enableRtl={enableRTL}
                    openComplete={(args: Object) => {
                        spreadsheetRef.current!.allowOpen = false;
                    }}
                />
            </div>
            <ToastComponent 
                ref={(toast: any) => toastInstance = toast!} 
                title="На жаль, Ви не можете редагувати документ"
                content={
                    documentLoader.document.metadata.isBlocked ?
                    "Цю версію документа було заблоковано користувачем" + ' ' + moment(new Date(documentLoader.document.metadata.blockedDate)).format('DD.MM.YYYY HH:mm')
                    :
                        !allowEdit && 
                            'У вас відсутні права на редагування документа. Зверніться будь ласка до автора документа або до адміністратора системи Contentum'
                }
                created={toastCreated} 
                showProgressBar={true}
                timeOut={10000}
                position={{ X: 'Right', Y: 'Top' }}
                enableRtl={enableRTL}
            />
            <div className="toastUtility"></div>
        </React.Fragment>
    )

}

export default ExcelApp;